import React from 'react';
import { NodeType, ProjectNode } from '@/shared/types/projectData';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { apiClient, API_ENDPOINTS } from '@/shared/apiClient';
import { transformProjectDataFromBackend } from '@/shared/utils/fieldNameTransform';
import { getNodeId, isNamespacedId } from '@/features/projects/utils/projectUtils';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { NodeMetadataEditor } from '@/features/projects/components/NodeMetadataEditor';
import { getVisibilityActionOptions } from '@/features/projects/utils/visibilityAuthority';
import { getProjectIdFromNodeId, useMultiProject } from '@/features/projects/MultiProjectContext';
import { nodeMetadataService } from '@/features/projects/services/nodeMetadataService';
import { approvalRequestService } from '@/features/projects/services/approvalRequestService';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { getPanelOwner, panelHasIntent, type PanelPersistenceState } from './TaskDetailsPanelPersistence';
import type { PendingProgress } from './TaskDetailsPanelHooks';
import { useAuth } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { getNodeAssigneeDisplayNames, getNodeAssignees, isAgentAssigneeId } from '@/shared/utils/projectDataUtils';
import { resolveDisplayAssigneeForViewer, shouldRedactAgentAssigneesForRole } from '@/features/projects/helpers/agentAssigneeRedaction';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';
import { useProjectRoleCheck } from '@/features/gantt/hooks/useProjectRoleCheck';
import { getVisibleNodeTypeOptions } from '@/shared/utils/nodeTypeConversionPolicy';
import { isGanttDocumentRecord } from '@/features/gantt/utils/documentRecords';
import {
  authorityPolicyRoleOptions,
  getAuthorityPolicyRoleEditState,
  type NodeAuthorityRole,
} from '@/features/gantt/utils/nodeAuthority';

// Import extracted modules
import { TaskDetailsPanelProps } from './TaskDetailsPanelTypes';
import { getSuggestions } from './TaskDetailsPanelHelpers';
import { 
  createBasicHandlers, 
  createFieldHandlers, 
  createProgressHandlers, 
  createCollaborationHandlers 
} from './TaskDetailsPanelHandlers';
import { ProgressSection, MilestoneProgress } from './TaskDetailsPanelProgress';
import {
  TypeSelector,
  DescriptionSection,
  DatesSection,
  ProjectSection,
  StatusPrioritySection,
  AssigneeClientSection,
  TaggedPeopleSection,
  TagsSection,
  ChecklistSection
} from './TaskDetailsPanelSections';
import { DependenciesSection } from './TaskDetailsPanelDependencies';
import { TaskChatSection } from './TaskChatSection';
import { FileAttachmentSection } from './sections/FileAttachmentSection';
import { ModalWrapper, PanelHeader, PanelFooter, PanelContent } from './TaskDetailsPanelLayout';
import { 
  useTaskState, 
  useModalAnimation, 
  useProgressDragging, 
  useSyncWithProjectData, 
  useProgressCleanup 
} from './TaskDetailsPanelHooks';
import { NoteDetailsPanel } from './NoteDetailsPanel';
import { AgentMissionSection } from '@/features/agentMission/AgentMissionSection';
import { supportsNodeChecklist } from './TaskDetailsPanelChecklist';

type MetadataEditorTheme = Pick<
  ReturnType<typeof useTheme>['theme'],
  'bgPrimary' | 'bgSecondary' | 'bgTertiary' | 'textPrimary' | 'textSecondary' | 'textMuted' | 'borderPrimary' | 'accentBlue'
>;

/* ============================================================================
 * MAIN COMPONENT
 * Core TaskDetailsPanel component with extracted logic
 * ============================================================================ */

const workflowText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

const AgentWorkLogSection: React.FC<{
  task: Partial<ProjectNode>;
  theme: ReturnType<typeof useTheme>['theme'];
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ task, theme, t }) => {
  const fields = task.customFields || {};
  const producer = workflowText(fields.producerProfileId);
  const reviewer = workflowText(fields.reviewerProfileId);
  const templateId = workflowText(fields.workflowTemplateId);
  const progressRule = workflowText(fields.progressRule);
  const artifactTarget = workflowText(fields.artifactTarget);

  if (!producer && !reviewer && !templateId && !progressRule) {
    return null;
  }

  return (
    <section className={cn("rounded-lg border p-3", theme.borderPrimary, theme.bgSecondary)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className={cn("text-sm font-semibold", theme.textPrimary)}>{t('Agent Work Log')}</h3>
        {templateId && (
          <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-800 dark:text-sky-300">
            {templateId.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="grid gap-2 text-xs sm:grid-cols-2">
        {producer && (
          <div>
            <span className={theme.textSecondary}>{t('Producer')}</span>
            <p className={cn("mt-0.5 font-medium", theme.textPrimary)}>{producer}</p>
          </div>
        )}
        {reviewer && (
          <div>
            <span className={theme.textSecondary}>{t('Reviewer')}</span>
            <p className={cn("mt-0.5 font-medium", theme.textPrimary)}>{reviewer}</p>
          </div>
        )}
        {artifactTarget && (
          <div>
            <span className={theme.textSecondary}>{t('Deliverable target')}</span>
            <p className={cn("mt-0.5 font-medium", theme.textPrimary)}>{artifactTarget}</p>
          </div>
        )}
        <div>
          <span className={theme.textSecondary}>{t('Progress')}</span>
          <p className={cn("mt-0.5 font-medium", theme.textPrimary)}>{Math.round(task.progress || 0)}%</p>
        </div>
      </div>
      {task.description && (
        <p className={cn("mt-3 whitespace-pre-line text-xs", theme.textSecondary)}>{task.description}</p>
      )}
      {progressRule && (
        <p className={cn("mt-2 text-xs", theme.textSecondary)}>{progressRule}</p>
      )}
    </section>
  );
};

const TaskDetailsPanelBase: React.FC<TaskDetailsPanelProps> = ({ 
  task, 
  onClose,
  onBeforeCreate,
}) => {
  /* ==========================================
   * HOOKS & CONTEXT
   * ========================================== */
  
  const { theme } = useTheme();
  const { t } = useSettings();
  const { updateNode, createNode, deleteNode, projectData, updateProgress: rawUpdateProgress } = useProjectData();
  const persistenceRef = React.useRef<PanelPersistenceState | null>(null);
  const latestEditedTaskRef = React.useRef<Partial<ProjectNode>>(task);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const panelSession = getAuthRuntimeSessionIdentity();
  const panelViewAs = getAdminViewAsUserId();
  const pendingProgressRef = React.useRef<PendingProgress | null>(null);
  const updateProgress = React.useCallback((id: string, value: number) => {
    pendingProgressRef.current = null;
    try {
      return Promise.resolve(rawUpdateProgress(id, value)).catch(error => {
        if (panelMountedRef.current) setSaveError(error instanceof Error ? error.message : t('Failed to save changes'));
      });
    } catch (error) {
      if (panelMountedRef.current) setSaveError(error instanceof Error ? error.message : t('Failed to save changes'));
      return Promise.resolve();
    }
  }, [rawUpdateProgress, t]);
  const {
    availableProjects,
    visibleProjects,
    getTaggableUsers,
    getProjectAgents,
    getAgentProfile,
    getAssigneeDisplayName,
  } = useMultiProject();
  const { hasPermission, user, actualUser } = useAuth();
  // Older consumers omit actualUser; explicit null still means no authenticated actor.
  const actor = actualUser === undefined ? user : actualUser;
  const ownerKey = actor?.id && user?.id ? JSON.stringify([actor.id, user.id]) : '';
  const latestOwnerKeyRef = React.useRef(ownerKey);
  latestOwnerKeyRef.current = ownerKey;
  const persistenceOwner = ownerKey && task.id ? getPanelOwner(ownerKey, task) : undefined;
  const canExecuteAgents = hasPermission('agents', 'execute');
  const { canEditProject, getProjectRole, canLinkDependencyNodes } = useProjectRoleCheck({ availableProjects, user, nodes: projectData?.nodes || [] });
  const suggestionRef = React.useRef<object | null>(null);
  const suggestionSourceRef = React.useRef('');
  const panelMountedRef = React.useRef(false);
  React.useEffect(() => { panelMountedRef.current = true; return () => { panelMountedRef.current = false; suggestionRef.current = null; }; }, []);
  const [isSuggestingMetadata, setIsSuggestingMetadata] = React.useState(false);
  const [metadataSuggestionRationale, setMetadataSuggestionRationale] = React.useState<string | null>(null);
  const [applyMetadataToDescendants, setApplyMetadataToDescendants] = React.useState(
    task.type === 'group' || task.type === 'milestone'
  );
  
  /* ==========================================
   * CUSTOM HOOKS
   * ========================================== */
  
  const {
    editedTask,
    setEditedTask,
    hasChanges,
    setHasChanges,
    currentChecklist,
    setCurrentChecklist,
    lastLocalUpdate,
    setLastLocalUpdate
  } = useTaskState(task, persistenceOwner);
  
  const {
    isOpen,
    setIsOpen,
    modalHeight,
    setModalHeight,
    modalWidth,
    setModalWidth,
    modalRef
  } = useModalAnimation();
  
  const {
    tempProgress,
    setTempProgress,
    isDraggingProgress,
    setIsDraggingProgress
  } = useProgressDragging();
  latestEditedTaskRef.current = editedTask;
  const ownsPanelSession = React.useCallback(() => latestOwnerKeyRef.current === ownerKey && getAuthRuntimeSessionIdentity() === panelSession && getAdminViewAsUserId() === panelViewAs, [ownerKey, panelSession, panelViewAs]);
  const readSavedTask = React.useCallback(async (projectId: string, nodeId: string) => {
    if (!ownsPanelSession()) throw new Error('The active project or account changed. Please try again.');
    const response = await apiClient.get<{ data: { project: unknown } }>(API_ENDPOINTS.projects.getById(projectId));
    if (!ownsPanelSession()) throw new Error('The active project or account changed. Please try again.');
    const saved = transformProjectDataFromBackend(response.data.project).nodes.find((node: ProjectNode) => getNodeId(node.id) === getNodeId(nodeId));
    if (!saved) throw new Error('Saved task was not found. Refresh and try again.');
    return { ...saved, projectId };
  }, [ownsPanelSession]);
  const lastCommittedTitleRef = React.useRef(task.title || '');
  const titleDraftStateRef = React.useRef({
    active: !!persistenceOwner && 'title' in persistenceOwner.activeVersions,
    hasChangesBeforeDraft: !!persistenceOwner && Object.keys(persistenceOwner.fields).length > 0,
  });
  const pendingTitleAckRef = React.useRef<{
    nodeId: string;
    title: string;
    committedAt: number;
  } | null>(null);

  React.useEffect(() => {
    // A parent snapshot is authoritative only when we are not preserving a
    // local title draft. This prevents a stale project-data echo from
    // replacing the pre-edit title used by Escape.
    if (!titleDraftStateRef.current.active && !pendingTitleAckRef.current) {
      lastCommittedTitleRef.current = editedTask.title || '';
    }
  }, [task.id, editedTask.title]);


  const handleModalEscape = React.useCallback(() => {
    const activeElement = document.activeElement;
    const isFocusedTitleDraft =
      activeElement instanceof HTMLInputElement &&
      activeElement.id === 'task-details-title-input' &&
      titleDraftStateRef.current.active;
    if (isFocusedTitleDraft) {
      // The focused input owns the cancellation. Yielding here allows its
      // handler to perform exactly one restore without closing the modal.
      return false;
    }
    // Save any pending progress before closing via ESC
    if (isDraggingProgress && tempProgress !== null && editedTask.id) {
      setLastLocalUpdate(Date.now());
      updateProgress(editedTask.id, tempProgress);
      setTempProgress(null);
      setIsDraggingProgress(false);
    }
    onClose();
  }, [
    editedTask.id,
    isDraggingProgress,
    onClose,
    setIsDraggingProgress,
    setLastLocalUpdate,
    setTempProgress,
    tempProgress,
    updateProgress,
  ]);

  // Register ESC handler for this panel using centralized manager
  useModalEscape('task-details-panel', handleModalEscape, {
    priority: 250, // High priority for detail panels
    enabled: isOpen
  });

  /* ==========================================
   * DATA SYNCHRONIZATION
   * ========================================== */
  
  useSyncWithProjectData(
    editedTask,
    setEditedTask,
    setCurrentChecklist,
    setTempProgress,
    lastLocalUpdate,
    isDraggingProgress,
    hasChanges,
    projectData,
    pendingTitleAckRef,
    persistenceOwner,
  );

  /* ==========================================
   * COMPUTED VALUES
   * ========================================== */

  const isNewTask = !task.id;
  const isDocumentDisplayRecord = isGanttDocumentRecord(editedTask as ProjectNode);
  const itemType: NodeType = isDocumentDisplayRecord ? 'document' : editedTask.type || 'task';
  const supportsChecklist = supportsNodeChecklist(itemType);
  const isAdmin = user?.role === 'admin';
  const editableProjects = visibleProjects.filter((project) => isAdmin || canEditProject(project.projectId));
  const canCreateThisTask = editableProjects.length > 0;
  const projectOptions = editableProjects.map((project) => ({
    projectId: project.projectId,
    projectName: project.projectName,
  }));
  const requiresProjectSelection = isNewTask && projectOptions.length > 1;
  // A new task has no id, so the namespaced-id fallback would yield the
  // placeholder 'default'. While the user still has to choose a project the
  // draft has no project at all; the pinned project is not preselected.
  const taskProjectId =
    editedTask.projectId ||
    (isNamespacedId(editedTask.id) ? getProjectIdFromNodeId(editedTask.id) : undefined) ||
    (requiresProjectSelection ? undefined : projectData?.projectId);
  const canUpdateThisTask = isNewTask
    ? canCreateThisTask
    : (!!taskProjectId && canEditProject(taskProjectId, editedTask, 'update'));
  const canMoveThisTask = isNewTask
    ? canCreateThisTask
    : (!!taskProjectId && canEditProject(taskProjectId, editedTask, 'move') && editableProjects.length > 0);
  const canEditThisTask = canUpdateThisTask && !persistenceOwner?.undoRequested;
  const tagInputOwner = JSON.stringify([ownerKey, panelSession, panelViewAs, taskProjectId, editedTask.id]);
  const [tagInput, setTagInput] = React.useState<{ owner: string; value: string; tags: string[] } | null>(null);
  const tagInputIsCurrent = tagInput?.owner === tagInputOwner
    && JSON.stringify(tagInput.tags) === JSON.stringify(editedTask.tags || []);
  const tagInputValue = tagInputIsCurrent ? tagInput.value : (editedTask.tags || []).join(', ');
  React.useEffect(() => {
    if (tagInput && !tagInputIsCurrent) setTagInput(null);
  }, [tagInput, tagInputIsCurrent]);
  const canManageSubtreeAuthority = !!taskProjectId
    && editedTask.type === 'group'
    && canEditProject(taskProjectId, editedTask, 'manage_policy');
  const canOverrideOwnSubtreeAuthority = !!taskProjectId && (isAdmin || getProjectRole(taskProjectId) === 'owner');
  const canViewInternalMetadata = (user?.role === 'admin' || user?.role === 'employee') && canEditThisTask;
  const selectedProjectInfo = visibleProjects.find((project) => project.projectId === taskProjectId);
  const selectedProjectAssigneeId = (
    selectedProjectInfo as {
      projectAssigneeId?: string | null;
      project_assignee_id?: string | null;
      metadata?: { settings?: { projectAssigneeId?: string | null } };
    } | undefined
  )?.projectAssigneeId
    || (
      selectedProjectInfo as {
        project_assignee_id?: string | null;
        metadata?: { settings?: { projectAssigneeId?: string | null } };
      } | undefined
    )?.project_assignee_id
    || (
      selectedProjectInfo as { metadata?: { settings?: { projectAssigneeId?: string | null } } } | undefined
    )?.metadata?.settings?.projectAssigneeId
    || null;
  const mentionUsers = getTaggableUsers(taskProjectId);
  const shouldRedactAgentAssignees = shouldRedactAgentAssigneesForRole(user?.role);
  const resolveAssigneeLabel = React.useCallback(
    (assignee: string) => {
      const displayAssignee = resolveDisplayAssigneeForViewer(assignee, {
        shouldRedact: shouldRedactAgentAssignees,
        node: editedTask,
        nodes: projectData?.nodes || [],
        projectAssigneeId: selectedProjectAssigneeId,
        projectMembers: mentionUsers,
      });
      if (!displayAssignee) return t('Unassigned');
      return isAgentAssigneeId(displayAssignee) ? t('Agent') : getAssigneeDisplayName(displayAssignee);
    },
    [
      editedTask,
      getAssigneeDisplayName,
      mentionUsers,
      projectData?.nodes,
      selectedProjectAssigneeId,
      shouldRedactAgentAssignees,
      t,
    ],
  );
  const assigneeLabels = getNodeAssigneeDisplayNames(editedTask as ProjectNode, resolveAssigneeLabel);
  const mentionLabels = (editedTask.mentions || []).map((mention) => getAssigneeDisplayName(mention));
  const projectAgents = React.useMemo(
    () => (!shouldRedactAgentAssignees && (itemType === 'task' || itemType === 'group') ? getProjectAgents(taskProjectId) : []),
    [getProjectAgents, itemType, shouldRedactAgentAssignees, taskProjectId],
  );
  const peopleAssigneeIds = React.useMemo(
    () => mentionUsers.map((entry) => entry.id),
    [mentionUsers],
  );
  const agentAssigneeIds = React.useMemo(
    () => projectAgents.map((agent) => agent.assigneeId),
    [projectAgents],
  );
  const assigneeSuggestions = React.useMemo(
    () => [...peopleAssigneeIds, ...agentAssigneeIds],
    [agentAssigneeIds, peopleAssigneeIds],
  );
  const assignedAgent = shouldRedactAgentAssignees ? undefined : getAgentProfile(getNodeAssignees(editedTask as ProjectNode)[0]);
  const assigneeSuggestionGroups = React.useMemo(
    () => [
      { label: t('People'), suggestions: peopleAssigneeIds },
      { label: t('Agents'), suggestions: agentAssigneeIds },
    ].filter((group) => group.suggestions.length > 0),
    [agentAssigneeIds, peopleAssigneeIds, t],
  );
  const supportsMetadataCascade = itemType === 'group' || itemType === 'milestone';
  const visibilityActionOptions = React.useMemo(
    () =>
      getVisibilityActionOptions({
        role: user?.role,
        currentVisibilityTier: editedTask.visibilityTier,
        isCreate: isNewTask,
      }),
    [editedTask.visibilityTier, isNewTask, user?.role],
  );
  const typeSelectorOptions = React.useMemo<NodeType[]>(
    () => {
      const standardOptions = getVisibleNodeTypeOptions(
        editedTask,
        projectData?.nodes || [],
        ['task', 'group', 'milestone'],
      );
      return isDocumentDisplayRecord ? ['document'] : standardOptions;
    },
    [editedTask, isDocumentDisplayRecord, projectData?.nodes],
  );
  const authorityEntries = React.useMemo(
    () => editedTask.authorityEntries || [],
    [editedTask.authorityEntries],
  );
  const authorityRoleByUser = React.useMemo(() => {
    const roleMap = new Map<string, NodeAuthorityRole>();
    authorityEntries.forEach((entry) => {
      if (entry.userId && entry.role) {
        roleMap.set(entry.userId, entry.role);
      }
    });
    return roleMap;
  }, [authorityEntries]);
  const shouldLockOwnAuthorityControls = canManageSubtreeAuthority && !canOverrideOwnSubtreeAuthority;
  const policyProjectRole = taskProjectId ? getProjectRole(taskProjectId) : undefined;
  const getPolicyRoleEditState = React.useCallback((
    memberId: string,
    currentRole?: NodeAuthorityRole | null,
    isAuthorityOwner = false,
  ) => getAuthorityPolicyRoleEditState({
    nodes: projectData?.nodes || [],
    node: editedTask as ProjectNode,
    targetUserId: memberId,
    currentRole,
    isAuthorityOwner,
    projectRole: policyProjectRole,
    user,
  }), [editedTask, policyProjectRole, projectData?.nodes, user]);

  React.useEffect(() => {
    setApplyMetadataToDescendants(task.type === 'group' || task.type === 'milestone');
  }, [task.id, task.type]);

  /* ==========================================
   * EVENT HANDLERS
   * ========================================== */
  
  // Create handler dependencies object
  const handlerDeps = {
    ownerKey, persistenceRef, ownsSession: ownsPanelSession, isPanelMounted: () => panelMountedRef.current, getEditedTask: () => latestEditedTaskRef.current, setSaveError, readSavedTask,
    // State setters
    setEditedTask,
    setHasChanges,
    setIsOpen,
    setModalHeight,
    setModalWidth,
    setCurrentChecklist,
    setLastLocalUpdate,
    setTempProgress,
    setIsDraggingProgress,
    
    // Context functions
    updateNode,
    deleteNode,
    createNode,
    updateProgress,
    hasPermission,
    t,
    requireProjectSelectionForCreate: requiresProjectSelection,
    canCreateTask: canCreateThisTask,
    canUpdateTask: canUpdateThisTask,
    canMoveTask: canMoveThisTask,
    projectNodes: projectData?.nodes || [],
    
    // Refs
    modalRef,
    
    // Callbacks
    onClose,
    onBeforeCreate,
    
    // State values
    task,
    editedTask,
    isDraggingProgress,
    tempProgress,
    hasChanges,
    lastCommittedTitleRef,
    titleDraftStateRef,
    pendingTitleAckRef,
  };
  
  React.useEffect(() => {
    if (!persistenceOwner) return;
    let checklistSignature = JSON.stringify(persistenceOwner.checklist?.desired);
    const refresh = () => {
      if (!panelMountedRef.current || !ownsPanelSession()) return;
      setSaveError(persistenceOwner.error);
      setHasChanges(panelHasIntent(persistenceOwner));
      const nextChecklistSignature = JSON.stringify(persistenceOwner.checklist?.desired);
      const checklistChanged = nextChecklistSignature !== checklistSignature && !!persistenceOwner.checklist;
      checklistSignature = nextChecklistSignature;
      setEditedTask(previous => ({ ...previous, ...persistenceOwner.fields, ...persistenceOwner.activeFields,
        ...(checklistChanged ? { checklist: persistenceOwner.checklist!.desired } : {}) }));
      if (checklistChanged) setCurrentChecklist(persistenceOwner.checklist!.desired);
    };
    persistenceOwner.listeners.add(refresh);
    setSaveError(persistenceOwner.error);
    setHasChanges(panelHasIntent(persistenceOwner));
    return () => { persistenceOwner.listeners.delete(refresh); };
  }, [persistenceOwner, ownsPanelSession, setEditedTask, setHasChanges, setCurrentChecklist]);

  // Create all handlers
  const { handleClose, handleUndo, handleSave } = createBasicHandlers(handlerDeps);
  const {
    handleFieldChange,
    handleTitleCommit,
    handleTitleCancel,
    handleProjectChange,
    handleBatchFieldChanges,
    handleTagsChange: persistTagsChange,
    handleRetrySave,
  } = createFieldHandlers(handlerDeps);
  const handleTagsChange = (value: string) => {
    if (!canEditThisTask || !ownsPanelSession()) return;
    const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
    setTagInput({ owner: tagInputOwner, value, tags });
    if (JSON.stringify(tags) !== JSON.stringify(editedTask.tags || [])) persistTagsChange(value);
  };
  const { handleProgressStart, handleProgressChange, handleProgressEnd } = createProgressHandlers(handlerDeps);
  const { handleAssigneesChange, handleChecklistChange } = createCollaborationHandlers(handlerDeps);

  const handleAuthorityOwnerChange = React.useCallback((ownerId: string) => {
    if (shouldLockOwnAuthorityControls) {
      return;
    }
    const nextOwnerId = ownerId || null;
    handleBatchFieldChanges({
      authorityOwnerId: nextOwnerId,
      authorityEntries: authorityEntries.filter((entry) => entry.userId !== nextOwnerId),
    });
  }, [
    authorityEntries,
    handleBatchFieldChanges,
    shouldLockOwnAuthorityControls,
  ]);

  const handleAuthorityRoleChange = React.useCallback((
    userId: string,
    role: '' | NodeAuthorityRole,
  ) => {
    const currentRole = authorityRoleByUser.get(userId) || null;
    const editState = getPolicyRoleEditState(
      userId,
      currentRole,
      userId === editedTask.authorityOwnerId,
    );
    if (!editState.canEdit || !editState.allowedRoles.includes(role)) {
      return;
    }
    const nextEntries = authorityEntries.filter((entry) => entry.userId !== userId);
    if (role && userId !== editedTask.authorityOwnerId) {
      nextEntries.push({ userId, role });
    }
    handleBatchFieldChanges({ authorityEntries: nextEntries });
  }, [
    authorityEntries,
    authorityRoleByUser,
    editedTask.authorityOwnerId,
    getPolicyRoleEditState,
    handleBatchFieldChanges,
  ]);

  const handleMetadataChanges = React.useCallback((changes: Partial<ProjectNode>) => {
    const nextChanges: Partial<ProjectNode> & { applyToDescendants?: boolean } = { ...changes };
    if (supportsMetadataCascade) {
      nextChanges.applyToDescendants = applyMetadataToDescendants;
    }
    handleBatchFieldChanges(nextChanges);
  }, [applyMetadataToDescendants, handleBatchFieldChanges, supportsMetadataCascade]);

  const suggestionSource = JSON.stringify([taskProjectId, editedTask.id, editedTask.type, editedTask.title, editedTask.description, editedTask.parentId, editedTask.workstream, editedTask.developmentRoles, editedTask.visibilityTier, editedTask.visibleToAccountIds, applyMetadataToDescendants]);
  suggestionSourceRef.current = suggestionSource;
  const suggestionAllowedRef = React.useRef(canEditThisTask);
  suggestionAllowedRef.current = canEditThisTask;

  const handleSuggestMetadata = React.useCallback(async () => {
    const projectId = taskProjectId || projectData?.projectId;
    if (!projectId || !canEditThisTask || suggestionRef.current) return;
    const operation = {};
    suggestionRef.current = operation;
    const source = suggestionSource;
    const session = getAuthRuntimeSessionIdentity();
    const viewAs = getAdminViewAsUserId();
    const current = () => panelMountedRef.current && suggestionRef.current === operation && suggestionSourceRef.current === source && suggestionAllowedRef.current && getAuthRuntimeSessionIdentity() === session && getAdminViewAsUserId() === viewAs;
    setIsSuggestingMetadata(true);
    try {
      const suggestion = await nodeMetadataService.suggest(projectId, {
        nodeType: itemType as 'group' | 'task' | 'milestone' | 'document' | 'report' | 'checklist',
        title: editedTask.title || '',
        description: editedTask.description || '',
        contentExcerpt: editedTask.description || '',
        parentId: editedTask.parentId || null,
      });
      if (!current()) return;
      setMetadataSuggestionRationale(suggestion.rationale || null);
      handleMetadataChanges({
        workstream: suggestion.workstream,
        developmentRoles: suggestion.development_roles,
        visibilityTier: suggestion.visibility_tier,
        // Suggestions classify the task; they do not rewrite its account
        // allowlist, which remains an explicit administrator-only operation.
        classificationSource: suggestion.classification_source,
        classificationConfidence: suggestion.classification_confidence,
      });
    } catch (error) {
      if (current()) setMetadataSuggestionRationale(error instanceof Error ? error.message : t('Failed to suggest metadata'));
    } finally {
      if (panelMountedRef.current && suggestionRef.current === operation) { suggestionRef.current = null; setIsSuggestingMetadata(false); }
    }
  }, [canEditThisTask, suggestionSource, editedTask.description, editedTask.parentId, editedTask.title, handleMetadataChanges, itemType, projectData?.projectId, t, taskProjectId]);


  useProgressCleanup(isDraggingProgress, tempProgress, editedTask.id, updateProgress, pendingProgressRef);

  /* ==========================================
   * RENDER
   * ========================================== */
  
  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      modalRef={modalRef}
      modalHeight={modalHeight}
      modalWidth={modalWidth}
      theme={theme}
    >
      {persistenceOwner?.undoRequested && !saveError && (
        <div role="status" className={cn('mx-4 mt-3 text-sm', theme.textSecondary)}>{t('Undoing changes...')}</div>
      )}
      {saveError && <div role="alert" className={cn('mx-4 mt-3 flex items-center gap-3 rounded border p-3 text-sm', theme.borderPrimary, theme.textPrimary)}>
        <span className="flex-1">{saveError}</span>
        <button type="button" disabled={!canUpdateThisTask} onClick={() => void handleRetrySave()}>{t('Retry')}</button>
      </div>}
      <PanelHeader
        title={editedTask.title || ''}
        hasChanges={hasChanges}
        canUndo={hasChanges || !!persistenceOwner?.undo}
        undoTitle={t('Undo Changes')}
        onTitleChange={(value) => handleFieldChange('title', value)}
        onTitleCommit={handleTitleCommit}
        onTitleCancel={handleTitleCancel}
        onUndo={handleUndo}
        editable={canEditThisTask}
        theme={theme}
      />

      <PanelContent>

              {/* Type Selector */}
              {canEditThisTask && (
                <TypeSelector
                  itemType={itemType}
                  availableTypes={typeSelectorOptions}
                  onTypeChange={(type) => handleFieldChange('type', type)}
                  theme={theme}
                />
              )}

              {/* Description */}
              <DescriptionSection
                description={editedTask.description || ''}
                onDescriptionChange={(value, mentionIds) =>
                  handleBatchFieldChanges({ description: value, mentions: mentionIds })
                }
                mentionUsers={mentionUsers}
                readOnly={!canEditThisTask}
                theme={theme}
              />
          
              <div className="space-y-6">
                {itemType === 'task' && (
                  <>
                    {projectOptions.length > 1 && canMoveThisTask && (
                      <ProjectSection
                        projectId={taskProjectId}
                        projectOptions={projectOptions}
                        onProjectChange={handleProjectChange}
                        theme={theme}
                      />
                    )}

                    {/* Dates */}
                    <DatesSection
                      startDate={editedTask.startDate}
                      endDate={editedTask.endDate}
                      dueDate={editedTask.dueDate}
                      itemType={itemType}
                      onFieldChange={handleFieldChange}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />
                    
                    {/* Status and Priority */}
                    <StatusPrioritySection
                      statusReadOnly={currentChecklist.length > 0}
                      status={editedTask.status}
                      priority={editedTask.priority}
                      itemType={itemType}
                      onFieldChange={handleFieldChange}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />
                    
                    {/* Progress */}
                    {canEditThisTask && currentChecklist.length === 0 ? (
                      <ProgressSection
                        progress={editedTask.progress || 0}
                        tempProgress={tempProgress}
                        isDraggingProgress={isDraggingProgress}
                        onProgressStart={handleProgressStart}
                        onProgressChange={handleProgressChange}
                        onProgressEnd={handleProgressEnd}
                        theme={theme}
                      />
                    ) : (
                      <div className="space-y-2">
                        <label className={cn("text-sm font-medium", theme.textSecondary)}>{t('Progress')}</label>
                        {currentChecklist.length > 0 && <p className={cn("text-xs", theme.textSecondary)}>{t('Progress is calculated from the checklist')}</p>}
                        <div className={cn("flex items-center gap-2")}> 
                          <input type="range" min="0" max="100" value={editedTask.progress || 0} disabled aria-label={t('Progress')} className={cn("flex-1 opacity-60 cursor-not-allowed", "appearance-none bg-gray-200 dark:bg-gray-700 rounded-lg h-2")} />
                          <span className={cn("text-sm", theme.textSecondary)}>{editedTask.progress || 0}%</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {supportsChecklist && (
                  <ChecklistSection
                    checklist={currentChecklist}
                    onChecklistChange={handleChecklistChange}
                    readOnly={!canEditThisTask}
                    theme={theme}
                  />
                )}

                {itemType === 'milestone' && (
                  <>
                    <DatesSection
                      date={editedTask.date}
                      itemType={itemType}
                      onFieldChange={handleFieldChange}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />
                    {canEditThisTask ? (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: theme.bgSecondary }}>
                        <MilestoneProgress
                          progress={editedTask.progress || 0}
                          onProgressChange={(value) => handleFieldChange('progress', value)}
                          theme={theme}
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className={cn("text-sm font-medium", theme.textSecondary)}>{t('Progress')}</label>
                        {currentChecklist.length > 0 && <p className={cn("text-xs", theme.textSecondary)}>{t('Progress is calculated from the checklist')}</p>}
                        <div className={cn("flex items-center gap-2")}>
                          <input type="range" min="0" max="100" value={editedTask.progress || 0} disabled aria-label={t('Progress')} className={cn("flex-1 opacity-60 cursor-not-allowed", "appearance-none bg-gray-200 dark:bg-gray-700 rounded-lg h-2")} />
                          <span className={cn("text-sm", theme.textSecondary)}>{editedTask.progress || 0}%</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {itemType === 'document' && (
                  <>
                    {projectOptions.length > 1 && canMoveThisTask && (
                      <ProjectSection
                        projectId={taskProjectId}
                        projectOptions={projectOptions}
                        onProjectChange={handleProjectChange}
                        theme={theme}
                      />
                    )}

                    <DatesSection
                      startDate={editedTask.startDate}
                      endDate={editedTask.endDate}
                      dueDate={editedTask.dueDate}
                      itemType={itemType}
                      onFieldChange={handleFieldChange}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />
                  </>
                )}

                {(itemType === 'task' || itemType === 'group' || itemType === 'milestone' || itemType === 'document') && (
                  <>
                    {(itemType === 'task' || itemType === 'group' || itemType === 'milestone') && (
                      <>
                        {/* Assignee and Client */}
                        <AssigneeClientSection
                          assignees={getNodeAssignees(editedTask as ProjectNode)}
                          assigneeLabels={assigneeLabels}
                          projectClient={selectedProjectInfo?.client || editedTask.projectClient || projectData?.client}
                          onAssigneesChange={handleAssigneesChange}
                          assigneeSuggestions={assigneeSuggestions}
                          assigneeSuggestionGroups={assigneeSuggestionGroups}
                          assigneeLabelResolver={resolveAssigneeLabel}
                          readOnly={!canEditThisTask}
                          theme={theme}
                        />

                        {itemType === 'task' && taskProjectId && editedTask.id ? (
                          <TaskChatSection
                            projectId={taskProjectId}
                            taskId={editedTask.id}
                            assignedAgent={assignedAgent}
                            readOnly={!canUpdateThisTask}
                            theme={theme}
                          />
                        ) : null}
                      </>
                    )}

                    <TaggedPeopleSection
                      mentions={editedTask.mentions || []}
                      mentionLabels={mentionLabels}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />

                    {canManageSubtreeAuthority && (
                      <section className="space-y-3">
                        <div>
                          <label className={cn("text-sm font-medium", theme.textSecondary)}>
                            {t('Subtree policy')}
                          </label>
                          <p className={cn("mt-1 text-xs", theme.textMuted)}>
                            {t('Delegate edit authority only inside this group.')}
                          </p>
                        </div>
                        <div className="grid gap-3">
                          <label className="space-y-1">
                            <span className={cn("text-xs font-medium", theme.textSecondary)}>{t('Owner')}</span>
                            <select
                              value={editedTask.authorityOwnerId || ''}
                              onChange={(event) => handleAuthorityOwnerChange(event.target.value)}
                              disabled={shouldLockOwnAuthorityControls}
                              className={cn(
                                "h-9 w-full rounded-md border px-3 text-sm",
                                theme.bgSecondary,
                                theme.borderPrimary,
                                theme.textPrimary,
                                theme.focusRing,
                                shouldLockOwnAuthorityControls && "cursor-not-allowed opacity-70",
                              )}
                              title={shouldLockOwnAuthorityControls
                                ? t('Only the project owner can change the subtree owner.')
                                : undefined}
                            >
                              <option value="">{t('Inherited')}</option>
                              {mentionUsers.map((member) => (
                                <option
                                  key={member.id}
                                  value={member.id}
                                >
                                  {member.username || member.email || member.id}
                                </option>
                              ))}
                            </select>
                          </label>

                          {mentionUsers.length > 0 && (
                            <div className="space-y-1">
                              <span className={cn("text-xs font-medium", theme.textSecondary)}>
                                {t('Delegated roles')}
                              </span>
                              <div className={cn("divide-y rounded-md border", theme.borderPrimary)}>
                                {mentionUsers.map((member) => {
                                  const isOwner = member.id === editedTask.authorityOwnerId;
                                  const currentRole = isOwner
                                    ? 'subtree_owner' as NodeAuthorityRole
                                    : authorityRoleByUser.get(member.id) || null;
                                  const editState = getPolicyRoleEditState(member.id, currentRole, isOwner);
                                  const isPolicyLocked = isOwner || !editState.canEdit;
                                  const roleSelectTitle = isOwner
                                    ? t('Change the subtree owner from the Owner field.')
                                    : editState.reason === 'self'
                                      ? t('Your delegated authority is controlled by a higher owner.')
                                      : editState.reason === 'peer_or_higher'
                                        ? t('You can only manage delegated roles below your own role.')
                                        : editState.reason === 'owner'
                                          ? t('Only the project owner can change this subtree owner.')
                                          : editState.reason === 'no_permission'
                                            ? t('You do not have permission to manage this policy.')
                                            : undefined;
                                  const roleLabels: Record<'' | NodeAuthorityRole, string> = {
                                    '': t('Inherited'),
                                    viewer: t('Viewer'),
                                    editor: t('Editor'),
                                    subtree_owner: t('Subtree owner'),
                                  };
                                  return (
                                    <div
                                      key={member.id}
                                      className={cn(
                                        "flex min-w-0 items-center gap-3 px-3 py-2",
                                        isPolicyLocked && "opacity-75",
                                      )}
                                    >
                                      <span className={cn("min-w-0 flex-1 truncate text-sm", theme.textPrimary)}>
                                        {member.username || member.email || member.id}
                                      </span>
                                      <select
                                        value={currentRole || ''}
                                        disabled={isPolicyLocked}
                                        title={roleSelectTitle}
                                        onChange={(event) => handleAuthorityRoleChange(
                                          member.id,
                                          event.target.value as '' | NodeAuthorityRole,
                                        )}
                                        className={cn(
                                          "h-8 w-36 rounded-md border px-2 text-xs",
                                          theme.bgPrimary,
                                          theme.borderPrimary,
                                          theme.textPrimary,
                                          theme.focusRing,
                                          isPolicyLocked && "cursor-not-allowed opacity-70",
                                        )}
                                      >
                                        {authorityPolicyRoleOptions.map((option) => (
                                          <option
                                            key={option || 'inherited'}
                                            value={option}
                                            disabled={editState.canEdit && !editState.allowedRoles.includes(option)}
                                          >
                                            {roleLabels[option]}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                    
                    {/* Tags */}
                    <TagsSection
                      tags={canEditThisTask ? [tagInputValue] : editedTask.tags}
                      onTagsChange={handleTagsChange}
                      suggestions={getSuggestions('tag', (tagInputValue.split(',').pop() || '').trim(), projectData)}
                      readOnly={!canEditThisTask}
                      theme={theme}
                    />

                    <AgentWorkLogSection task={editedTask} theme={theme} t={t} />

                    {taskProjectId && (
                      <AgentMissionSection
                        projectId={taskProjectId}
                        canEdit={canEditThisTask}
                        canExecuteAgent={canExecuteAgents}
                      />
                    )}

                    {canViewInternalMetadata && (
                      <NodeMetadataEditor
                        projectId={taskProjectId}
                        nodeId={editedTask.id}
                        targetLabel={editedTask.title || 'task'}
                        workstream={editedTask.workstream}
                        developmentRoles={editedTask.developmentRoles}
                        visibilityTier={editedTask.visibilityTier}
                        visibilityOptions={visibilityActionOptions}
                        visibleToAccountIds={editedTask.visibleToAccountIds}
                        classificationConfidence={editedTask.classificationConfidence}
                        rationale={metadataSuggestionRationale}
                        readOnly={!canEditThisTask}
                        canManageVisibleToAccounts={isAdmin}
                        suggesting={isSuggestingMetadata}
                        showApplyToDescendants={supportsMetadataCascade && isAdmin}
                        applyToDescendants={applyMetadataToDescendants}
                        theme={{
                          bgPrimary: theme.bgPrimary, bgSecondary: theme.bgSecondary, bgTertiary: theme.bgTertiary,
                          textPrimary: theme.textPrimary, textSecondary: theme.textSecondary, textMuted: theme.textMuted,
                          borderPrimary: theme.borderPrimary, accentBlue: theme.accentBlue,
                        } satisfies MetadataEditorTheme}
                        onSuggest={handleSuggestMetadata}
                        onApplyToDescendantsChange={setApplyMetadataToDescendants}
                        onWorkstreamChange={(value) => handleMetadataChanges({ workstream: value })}
                        onDevelopmentRolesChange={(value) => handleMetadataChanges({ developmentRoles: value })}
                        onVisibilityTierChange={(value) => handleMetadataChanges({ visibilityTier: value })}
                        onRequestVisibilityTier={async ({ projectId, nodeId, visibilityTier, scope, rationale }) => {
                          await approvalRequestService.create({
                            projectId,
                            nodeId,
                            requestedVisibilityTier: visibilityTier,
                            scope,
                            rationale,
                          });
                        }}
                        onVisibleToAccountIdsChange={(value) => handleMetadataChanges({ visibleToAccountIds: value })}
                      />
                    )}

                    {(itemType !== 'document' || Boolean(editedTask.dependencies?.length)) && (
                      <DependenciesSection
                        canLinkDependency={node => canLinkDependencyNodes?.(editedTask, node) ?? false}
                        dependencies={editedTask.dependencies}
                        projectData={projectData}
                        editedTaskId={editedTask.id}
                        onDependenciesChange={(deps) => handleFieldChange('dependencies', deps || [])}
                        readOnly={!canEditThisTask}
                        theme={theme}
                      />
                    )}

                    {/* File Attachments Section */}
                    {editedTask.id && taskProjectId && (
                      <FileAttachmentSection
                        projectId={taskProjectId}
                        nodeId={editedTask.id}
                        nodeType={itemType}
                        canEdit={canEditThisTask}
                      />
                    )}
                  </>
                )}
              </div>
      </PanelContent>

      {isNewTask && canCreateThisTask && (
        <PanelFooter
          onSave={handleSave}
          disabled={requiresProjectSelection && !editedTask.projectId}
          disabledReason={requiresProjectSelection ? t('Select a project before saving this task.') : undefined}
          theme={theme}
        />
      )}
    </ModalWrapper>
  );
};

/* ============================================================================
 * MEMOIZATION & EXPORT
 * Performance optimization with React.memo
 * ============================================================================ */

const TaskDetailsPanelRouter: React.FC<TaskDetailsPanelProps> = (props) => {
  if (isGanttDocumentRecord(props.task as ProjectNode)) {
    return <NoteDetailsPanel {...props} />;
  }

  return <TaskDetailsPanelBase key={JSON.stringify([props.task.projectId, props.task.id || 'new', getAuthRuntimeSessionIdentity(), getAdminViewAsUserId()])} {...props} />;
};

// Keep the panel responsive to authoritative task-state updates while it is open.
export const TaskDetailsPanel = React.memo(TaskDetailsPanelRouter);
